import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, CornerDownLeft, MessageSquarePlus, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  instructionFieldName, instructionFieldNames, pickableStages, resolvePickedPipeline,
} from '@/lib/processOrder';

/**
 * The production pipeline, picked one process at a time.
 *
 * The number IS the control: typing one includes the process and fixes its
 * place in the running order; clearing it drops the process. Clicking the row
 * fills in the next free number. Only what is numbered runs — a numbered list is
 * never unresolvable, so there is no half-answered pipeline.
 *
 * A picked process can carry one instruction — "Matt BOPP 12 mic" — and it is
 * asked for on the process's own row. It used to be a row in the spec sheet, one
 * per process, so the same decision was asked twice and the two could disagree.
 *
 * Picking writes a bare "Yes", which is all the stage rule needs, and a box
 * containing the word "Yes" says nothing the green Yes beside it has not already
 * said. So the box appears only when there is a real instruction to show, or
 * when someone asks for one.
 *
 * Search is how a pipeline is actually built: a product type offers up to 45
 * processes, and hunting each one down a scrolling list is slower than saying
 * its name. Type, press Enter, the highlighted match is added and the box clears
 * itself ready for the next one — so a six-step pipeline is six words, hands
 * never leaving the keyboard.
 *
 * The arrows move that highlight. A word like "printing" names half a dozen
 * processes — four, five and six colour, screen, flex — and Enter alone can only
 * ever reach whichever of them sorts first, so picking any of the others meant
 * abandoning the keyboard and hunting the row. Down and Up walk the matches,
 * wrapping at both ends, and Enter takes whatever is lit.
 *
 * Packing and Dispatch are not on the list: every job is packed and every job
 * goes out, so they are appended whatever else was picked.
 *
 * How the numbering resolves to a pipeline lives in lib/processOrder — the
 * supervisor's phone editor picks against the same helpers.
 */

export { instructionFieldName, instructionFieldNames, pickableStages, resolvePickedPipeline };

// What picking writes: enough for the presence test, and nothing to read.
const PICKED = 'Yes';

function ProcessRow({ label, seq, on, target, rowRef, testId, onToggle, onSeqChange, field, value, onValueChange }) {
  const [noteOpen, setNoteOpen] = useState(false);

  // a dropped process takes its open box with it, so picking it again does not
  // reveal a box nobody asked for this time round
  useEffect(() => { if (!on) setNoteOpen(false); }, [on]);
  // a toggle-picked process has already said everything it can — the row's own
  // Yes is the whole answer
  const canNote = on && field && field.type !== 'toggle';
  const hasNote = canNote && value && value !== PICKED;
  const showNote = canNote && (hasNote || noteOpen);

  return (
    <div
      ref={rowRef}
      data-testid={testId}
      data-target={target ? 'true' : undefined}
      className={`border-b border-slate-200 ${
        target ? 'bg-blue-50 ring-1 ring-inset ring-brand-blue' : on ? 'bg-emerald-50' : ''
      }`}
    >
      <div className="grid grid-cols-[2.5rem_1fr_auto_2.75rem] items-stretch">
        <input
          type="number"
          min="1"
          value={seq ?? ''}
          onChange={e => onSeqChange(e.target.value)}
          aria-label={`${label} order`}
          placeholder="–"
          className={`w-full border-r border-slate-200 bg-white/70 text-center text-sm font-semibold tabular-nums focus:outline-none focus:ring-1 focus:ring-inset focus:ring-brand-blue ${
            on ? 'text-emerald-800' : 'text-slate-400 placeholder:text-slate-300'
          }`}
        />
        <button
          type="button"
          onClick={() => onToggle(!on)}
          aria-pressed={on}
          className="text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-blue"
        >
          <span className={`block truncate px-2 py-2 text-sm leading-tight ${on ? 'font-semibold text-emerald-900' : 'text-slate-700'}`}>
            {label}
          </span>
        </button>
        {canNote && !showNote ? (
          <button
            type="button"
            onClick={() => setNoteOpen(true)}
            aria-label={`Add an instruction for ${label}`}
            title="Say how it is to be done"
            className="flex items-center px-1.5 text-emerald-600/70 transition-colors hover:text-emerald-800"
          >
            <MessageSquarePlus className="h-3.5 w-3.5" />
          </button>
        ) : <span />}
        <span
          className={`flex items-center justify-center gap-0.5 border-l text-xs font-semibold ${
            on ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-200 text-slate-400'
          }`}
        >
          {on && <Check className="h-3 w-3" />}
          {on ? 'Yes' : target ? <CornerDownLeft className="h-3 w-3 text-brand-blue" /> : 'No'}
        </span>
      </div>

      {showNote && (
        <div className="px-2 pb-2 pl-[2.5rem]">
          <Input
            type="text"
            // "Yes" is the picked marker, not something anybody wrote
            value={value === PICKED ? '' : (value ?? '')}
            onChange={e => onValueChange(e.target.value || PICKED)}
            onBlur={() => setNoteOpen(false)}
            autoFocus={noteOpen && !hasNote}
            aria-label={`${label} instruction`}
            placeholder="How it is to be done — e.g. Matt BOPP 12 mic"
            className="h-8 border-slate-200 bg-white text-sm"
          />
        </div>
      )}
    </div>
  );
}

export default function ProcessPicker({
  rules, processOrder, onChange, hasType,
  fieldsByName = {}, specs = {}, onSpecChange,
}) {
  const [query, setQuery] = useState('');
  // null means "wherever typing put it"; an arrow key pins it to an index
  const [cursor, setCursor] = useState(null);
  const searchRef = useRef(null);
  const targetRef = useRef(null);

  const stages = useMemo(() => pickableStages(rules), [rules]);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return stages;
    return stages.filter(rule => rule.stage_name.toLowerCase().includes(term));
  }, [stages, query]);

  // Typing lands on the first match not already on the pipeline: searching for
  // something already picked is not an error, it just has nothing to add, and
  // stopping there would make Enter do nothing on a perfectly good search.
  const firstAddable = useMemo(() => {
    if (!matches.length) return -1;
    const idx = matches.findIndex(rule => processOrder[rule.stage_key] == null);
    return idx === -1 ? 0 : idx;
  }, [matches, processOrder]);

  // a new search starts over; the arrows take it from there
  useEffect(() => { setCursor(null); }, [query]);

  const active = cursor == null ? firstAddable : Math.min(cursor, matches.length - 1);
  const showTarget = query.trim() !== '' || cursor != null;
  const target = showTarget && active >= 0 ? matches[active] ?? null : null;
  const targetIsPicked = target ? processOrder[target.stage_key] != null : false;

  // the list scrolls and the product type offers up to 45 processes, so the
  // highlight has to bring itself into view or the arrows walk off screen
  useEffect(() => {
    targetRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active, query]);

  const nextSeq = () => Math.max(0, ...Object.values(processOrder)) + 1;

  // Picking a process fills its instruction with a bare "Yes" — enough for the
  // presence test, and replaced by the real instruction when someone types one.
  // Dropping it clears the field, so the specs never describe work nobody asked
  // for.
  const setPicked = (rule, picked) => {
    const name = instructionFieldName(rule);
    if (!name || !onSpecChange) return;
    if (picked) {
      if (!specs[name]) onSpecChange(name, PICKED);
    } else if (specs[name]) {
      onSpecChange(name, '');
    }
  };

  const pick = (rule, seq) => {
    onChange({ ...processOrder, [rule.stage_key]: seq });
    setPicked(rule, true);
  };

  const drop = (rule) => {
    const updated = { ...processOrder };
    delete updated[rule.stage_key];
    onChange(updated);
    setPicked(rule, false);
  };

  const toggle = (rule, next) =>
    (next ? pick(rule, processOrder[rule.stage_key] || nextSeq()) : drop(rule));

  const setSeq = (rule, raw) => {
    const n = parseInt(raw, 10);
    if (!raw || Number.isNaN(n) || n < 1) drop(rule);
    else pick(rule, n);
  };

  // Down and Up walk the matches and wrap at both ends — a search with three
  // hits is quicker to cycle than to steer.
  const move = (delta) => {
    if (!matches.length) return;
    const from = active < 0 ? 0 : active;
    setCursor((from + delta + matches.length) % matches.length);
  };

  // Enter adds the highlighted match and empties the box without giving up
  // focus, so the next process is just the next word typed.
  const onSearchKeyDown = (event) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); move(1); return; }
    if (event.key === 'ArrowUp') { event.preventDefault(); move(-1); return; }
    if (event.key === 'Escape') { setQuery(''); setCursor(null); return; }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    // a process already on the pipeline has nothing to add — its row says Yes
    if (!target || targetIsPicked) return;
    pick(target, nextSeq());
    setQuery('');
    setCursor(null);
    searchRef.current?.focus();
  };

  if (!hasType) {
    return (
      <p className="px-3 py-6 text-center text-sm text-slate-400">
        Pick an order type to see the processes it can run.
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative shrink-0 border-b border-slate-200 bg-slate-50 p-1.5">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input
          ref={searchRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          aria-label="Search processes"
          placeholder="Name a process, ↑↓ to choose, Enter to add…"
          className="h-8 border-slate-200 bg-white pl-8 pr-20 text-sm"
          data-testid="process-search"
        />
        {showTarget && (
          <span
            data-testid="process-search-hint"
            className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400"
          >
            {!matches.length ? 'no match'
              : targetIsPicked ? 'already added'
              : <span className="flex items-center gap-1 text-brand-blue">add <CornerDownLeft className="h-3 w-3" /></span>}
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto" data-testid="process-list">
        {matches.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-slate-400">
            No process here is called “{query.trim()}”.
          </p>
        ) : matches.map(rule => {
          const name = instructionFieldName(rule);
          const isTarget = target?.stage_key === rule.stage_key;
          return (
            <ProcessRow
              key={rule.stage_key}
              testId={`process-row-${rule.stage_key}`}
              label={rule.stage_name}
              seq={processOrder[rule.stage_key] ?? null}
              on={processOrder[rule.stage_key] != null}
              target={isTarget}
              rowRef={isTarget ? targetRef : undefined}
              onToggle={next => toggle(rule, next)}
              onSeqChange={raw => setSeq(rule, raw)}
              field={name ? fieldsByName[name] : null}
              value={name ? specs[name] : undefined}
              onValueChange={val => onSpecChange?.(name, val)}
            />
          );
        })}
      </div>
    </div>
  );
}
